import autobind from 'autobind-decorator';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { InputProps } from '../../../../common/components/form-elements/Input';
import { InputSelectProps } from '../../../../common/components/form-elements/InputSelect';
import * as Actions from '../../../../common/services/customer/Actions';
import * as CustomerService from '../../../../common/services/customer/Service';
import { CustomerActionPropTypes, CustomerError } from '../../../../common/services/customer/Types';
import { AddValueAndCareCardDTO } from '../../../../common/types/dto/AddValueAndCareCardDTO';
import { CustomerDataDTO } from '../../../../common/types/dto/CustomerDataDTO';
import { DealersDTO } from '../../../../common/types/dto/DealersDTO';
import { LookupValueDTO } from '../../../../common/types/dto/LookupValueDTO';
import { VehicleDataDTO } from '../../../../common/types/dto/VehicleDataDTO';
import { ConfigProp, RenderPropWithProps, RouteProps, TranslationFuncProp } from '../../../../common/types/prop-types/common';
import { RootState } from '../../../base/RootReducer';
import { config } from '../../../common/';
import { AppThunkDispatch } from '../../../common/types';

export interface NewCardProps extends ConfigProp {
    vin: InputProps;
    licensePlateString: InputProps;
    licensePlateForeign: InputProps;
    isForeignPlate: boolean;
    onIsForeignPlateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    licensePlateSelect: InputSelectProps;
    preferedDealer: InputSelectProps;
    newVehicleState: NewVehicleState;
    submitErrorText: string;
    isUserDataAvailable: boolean;

    currentStep: number;
    address: InputProps;
    addressNumber: InputProps;
    addressFloor: InputProps;
    city: InputProps;
    postalCode: InputProps;

    btnNext: {
        onClick: (event: React.MouseEvent<{}> | null) => void;
        value: string;
        disabled: boolean;
    };

    btnBack: {
        onClick: (event: React.MouseEvent<{}> | null) => void;
    };

    btnSubmit: {
        onClick: (event: React.MouseEvent<{}> | null, onSuccess?: () => void | null) => void;
        value: string;
        disabled: boolean;
    };
}

export interface NewVehicleState {
    isEditing: boolean;
    changeEditMode: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface NewCardContainerProps {
}

interface ReduxStateProps {
    isCommunicating: boolean;
    serviceStations: DealersDTO[];
    customerInfo: CustomerDataDTO | undefined;
}

interface ReduxDispatchProps {
    customerActions: typeof Actions;
    customerServiceActions: CustomerService.ServiceActions;
    dispatch: AppThunkDispatch;
}

interface NewCardContainerState {
    isEditing: boolean;
    currentStep: number;
    licensePlate: string;
    licensePlateValMsg: string;
    licensePlateForeign: string;
    isForeignPlate: boolean;
    vin: string;
    vinValMsg: string;
    preferedDealer: string;
    preferedDealerLabel: string;
    preferedDealerValMsg: string;
    vehicles: VehicleDataDTO[];
    submitErrorText: string;
    address: string;
    addressValMsg: string;
    addressNumber: string;
    addressNumberValMsg: string;
    addressFloor: string;
    addressFloorValMsg: string;
    city: string;
    cityValMsg: string;
    postalCode: string;
    postalCodeValMsg: string;
    isAddressPrefilled: boolean;
}

class NewCardContainer extends Component<
    NewCardContainerProps &
    TranslationFuncProp &
    RouteProps &
    RenderPropWithProps<NewCardProps> &
    ReduxStateProps &
    ReduxDispatchProps,
    NewCardContainerState
    > {
    public static propTypes = {
        t: PropTypes.func.isRequired,
        isCommunicating: PropTypes.bool.isRequired,
        routeProps: PropTypes.any.isRequired,
        customerCards: PropTypes.any,
        customerActions: CustomerActionPropTypes.isRequired,
        customerServiceActions: CustomerService.ServiceActionPropTypes.isRequired,
        customerInfo: PropTypes.any,
        serviceStations: PropTypes.array.isRequired,
        dispatch: PropTypes.func.isRequired,
        //
        children: PropTypes.func.isRequired,
    };

    public readonly state: NewCardContainerState = {
        isEditing: false,
        currentStep: 1,
        licensePlate: '',
        licensePlateValMsg: '',
        licensePlateForeign: '',
        isForeignPlate: false,
        vin: '',
        vinValMsg: '',
        vehicles: [],
        submitErrorText: '',
        preferedDealer: '',
        preferedDealerLabel: '',
        preferedDealerValMsg: '',
        address: '',
        addressValMsg: '',
        addressNumber: '',
        addressNumberValMsg: '',
        addressFloor: '',
        addressFloorValMsg: '',
        city: '',
        cityValMsg: '',
        postalCode: '',
        postalCodeValMsg: '',
        isAddressPrefilled: false,
    };

    public changeEditMode = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            isEditing: event.target.checked,
        });
    }

    public componentWillReceiveProps(nextProps: ReduxStateProps) {
        this.handleInit(nextProps);
    }

    public componentDidMount() {
        this.handleInit(this.props);
    }

    public handleInit(nextProps: ReduxStateProps) {
        if (nextProps.customerInfo) {
            this.setState({
                vehicles: nextProps.customerInfo.vehicles || this.state.vehicles,
            });
            if (!this.state.isAddressPrefilled) {
                const ci = nextProps.customerInfo;
                this.setState({
                    address: ci.address || '',
                    addressNumber: ci.addressNumber || '',
                    addressFloor: ci.addressFloor || '',
                    city: ci.city || '',
                    postalCode: ci.postalCode || '',
                    isAddressPrefilled: true,
                });
            }
        }
    }

    public validateForm() {
        if (this.props.isCommunicating) {
            return false;
        }
        let isValid: boolean = true;
        if (!this.state.isForeignPlate) {
            if (this.state.licensePlate === '' || this.state.licensePlate === undefined) {
                this.setState({ licensePlateValMsg: this.props.t('mandatory') });
                isValid = false;
            }
        } else { // Foreign plate
            if (this.state.licensePlateForeign === '' || this.state.licensePlateForeign === undefined) {
                this.setState({ licensePlateValMsg: this.props.t('mandatory') });
                isValid = false;
            }
        }
        if (this.state.preferedDealer === '' || this.state.preferedDealer === undefined) {
            this.setState({ preferedDealerValMsg: this.props.t('mandatory') });
            isValid = false;
        }
        if (this.state.vin && !config.Constants.Regex.VinValidation.test(this.state.vin)) {
            this.setState({ vinValMsg: this.props.t('signup:errors:vinValidationRegex') });
            isValid = false;
        }
        return isValid;
    }

    public validateAddressForm() {
        let isValid: boolean = true;
        if (this.state.address === '' || this.state.address === undefined) {
            this.setState({ addressValMsg: this.props.t('mandatory') });
            isValid = false;
        }
        if (this.state.addressNumber === '' || this.state.addressNumber === undefined) {
            this.setState({ addressNumberValMsg: this.props.t('mandatory') });
            isValid = false;
        }
        if (this.state.city === '' || this.state.city === undefined) {
            this.setState({ cityValMsg: this.props.t('mandatory') });
            isValid = false;
        }
        if (this.state.addressFloor === '' || this.state.addressFloor === undefined) {
            this.setState({ addressFloorValMsg: this.props.t('mandatory') });
            isValid = false;
        }
        if (this.state.postalCode === '' || this.state.postalCode === undefined) {
            this.setState({ postalCodeValMsg: this.props.t('mandatory') });
            isValid = false;
        }
        return isValid;
    }

    @autobind
    public onNext(e: React.MouseEvent<{}> | null) {
        if (e) {
            e.preventDefault();
        }
        if (!this.validateForm()) {
            return;
        }
        this.setState({ currentStep: 2 });
    }

    @autobind
    public onBack(e: React.MouseEvent<{}> | null) {
        if (e) {
            e.preventDefault();
        }
        this.setState({ currentStep: 1, submitErrorText: '' });
    }

    @autobind
    public async onSubmit(e: React.MouseEvent<{}> | null, onSuccess?: () => void | null) {
        if (e) {
            e.preventDefault();
        }
        if (!this.validateForm() || !this.validateAddressForm()) {
            return;
        }
        try {
            const addValueAndCareCardDTO: AddValueAndCareCardDTO = {
                licensePlate: this.state.licensePlate,
                vin: this.state.vin,
                preferedDealer: this.state.preferedDealer,
                newLicensePlate: this.state.isEditing,
                licensePlateForeign: this.state.licensePlateForeign,
                isForeignPlate: this.state.isForeignPlate,
                address: this.state.address,
                addressNumber: this.state.addressNumber,
                addressFloor: this.state.addressFloor,
                city: this.state.city,
                postalCode: this.state.postalCode,
            };
            await this.props.customerServiceActions.addValueAndCareCard(addValueAndCareCardDTO);
            this.setState({
                submitErrorText: '',
                isEditing: false,
                currentStep: 1,
            });
            this.props.dispatch(Notifications.success({
                title: this.props.t('success'),
                message: this.props.t('cards:newCard.success'),
                position: 'tc',
                autoDismiss: 5,
            }));
            this.handleInit(this.props);
            await this.props.customerServiceActions.customerData();
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            if (error instanceof CustomerError) {
                const exceptionType: string = error.customerErrorInfo.exceptionType || 'UnknownException';
                const exceptionErrorMsg: string = this.props.t(`exception:${exceptionType.split('.').pop()}`);
                this.setState({ submitErrorText: exceptionErrorMsg });
                this.props.dispatch(Notifications.error({
                    title: this.props.t('error'),
                    message: exceptionErrorMsg,
                    position: 'tc',
                    autoDismiss: 5,
                }));
            } else {
                throw error;
            }
        }
    }

    @autobind
    public onLicensePlateChange(event: any) {
        if (this.state.isEditing) {
            this.setState({ licensePlate: event.currentTarget.value });
        } else if (this.props.customerInfo && this.props.customerInfo.vehicles) {
            let newVin: string | null | undefined = '';
            this.props.customerInfo.vehicles.map((vehicle) => {
                if (vehicle.licensePlate === event.value) {
                    newVin = vehicle.vin;
                }
            });
            this.setState({
                licensePlate: event.value,
                vin: newVin,
            });
        }

        if (event.value !== '') {
            this.setState({ licensePlateValMsg: '' });
        } else {
            this.setState({ licensePlateValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onPreferedDealerChange(event: any) {
        this.setState({
            preferedDealer: event.value,
            preferedDealerLabel: event.label,
        });
        if (event.value !== '') {
            this.setState({ preferedDealerValMsg: '' });
        } else {
            this.setState({ preferedDealerValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onVinChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({
            vin: event.currentTarget.value,
            vinValMsg: '',
        });
    }

    @autobind
    public onLicensePlateForeignChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ licensePlateForeign: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ licensePlateValMsg: '' });
        } else {
            this.setState({ licensePlateValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onIsForeignPlateChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({
            licensePlateForeign: '',
            licensePlate: '',
            licensePlateValMsg: '',
            isForeignPlate: event.target.checked,
        });
    }

    @autobind
    public onAddressChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({
            address: event.currentTarget.value,
            addressValMsg: event.currentTarget.value !== '' ? '' : this.props.t('mandatory'),
        });
    }

    @autobind
    public onAddressNumberChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({
            addressNumber: event.currentTarget.value,
            addressNumberValMsg: event.currentTarget.value !== '' ? '' : this.props.t('mandatory'),
        });
    }

    @autobind
    public onAddressFloorChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({
            addressFloor: event.currentTarget.value,
        });
    }

    @autobind
    public onCityChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({
            city: event.currentTarget.value,
            cityValMsg: event.currentTarget.value !== '' ? '' : this.props.t('mandatory'),
        });
    }

    @autobind
    public onPostalCodeChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({
            postalCode: event.currentTarget.value,
            postalCodeValMsg: event.currentTarget.value !== '' ? '' : this.props.t('mandatory'),
        });
    }

    public render() {
        const licensePlateArray: Array<LookupValueDTO<string>> = [];
        if (this.props.customerInfo && this.props.customerInfo.vehicles) {
            this.props.customerInfo.vehicles.map((vehicle) => {
                if (vehicle.licensePlate) {
                    licensePlateArray.push({
                        label: vehicle.licensePlate,
                        value: vehicle.licensePlate,
                    });
                }
            });
        }
        const ci = this.props.customerInfo;
        const newProps: NewCardProps = {
            newVehicleState: {
                isEditing: this.state.isEditing,
                changeEditMode: this.changeEditMode,
            },
            licensePlateString: {
                inputProps: {
                    value: this.state.licensePlate,
                    onChange: this.onLicensePlateChange,
                    maxLength: 8,
                },
                errorText: this.state.licensePlateValMsg,
                hintText: '',
            },
            licensePlateForeign: {
                inputProps: {
                    value: this.state.licensePlateForeign,
                    onChange: this.onLicensePlateForeignChange,
                    maxLength: config.Validations.LicensePlateForeign.MaxLength,
                },
                errorText: this.state.licensePlateValMsg,
                hintText: '',
            },
            isForeignPlate: this.state.isForeignPlate,
            onIsForeignPlateChange: this.onIsForeignPlateChange,
            vin: {
                inputProps: {
                    value: this.state.vin,
                    onChange: this.onVinChange,
                    maxLength: 17,
                },
                errorText: this.state.vinValMsg,
                hintText: '',
            },
            preferedDealer: {
                inputProps: {
                    value: this.state.preferedDealer,
                    name: this.state.preferedDealerLabel,
                },
                onChange: this.onPreferedDealerChange,
                errorText: this.state.preferedDealerValMsg,
                hintText: '',
                options: this.props.serviceStations,
                placeholder: this.props.serviceStations.length > 0 ? this.props.t('cards:newCard.selectDealer') : this.props.t('loadingText'),
            },
            licensePlateSelect: {
                inputProps: {
                    value: this.state.licensePlate,
                },
                onChange: this.onLicensePlateChange,
                errorText: this.state.licensePlateValMsg,
                hintText: '',
                options: licensePlateArray,
                placeholder: !this.props.customerInfo ? this.props.t('loadingText') : licensePlateArray.length > 0 ? this.props.t('cards:newCard.selectPlate') : this.props.t('noOptions'),
            },
            submitErrorText: this.state.submitErrorText,
            btnSubmit: {
                onClick: this.onSubmit,
                value: this.props.t('cards:newCard.request'),
                disabled: this.props.isCommunicating,
            },
            currentStep: this.state.currentStep,
            address: {
                inputProps: {
                    value: this.state.address,
                    onChange: this.onAddressChange,
                },
                errorText: this.state.addressValMsg,
                hintText: '',
            },
            addressNumber: {
                inputProps: {
                    value: this.state.addressNumber,
                    onChange: this.onAddressNumberChange,
                },
                errorText: this.state.addressNumberValMsg,
                hintText: '',
            },
            addressFloor: {
                inputProps: {
                    value: this.state.addressFloor,
                    onChange: this.onAddressFloorChange,
                },
                errorText: this.state.addressFloorValMsg,
                hintText: '',
            },
            city: {
                inputProps: {
                    value: this.state.city,
                    onChange: this.onCityChange,
                },
                errorText: this.state.cityValMsg,
                hintText: '',
            },
            postalCode: {
                inputProps: {
                    value: this.state.postalCode,
                    onChange: this.onPostalCodeChange,
                },
                errorText: this.state.postalCodeValMsg,
                hintText: '',
            },
            btnNext: {
                onClick: this.onNext,
                value: this.props.t('cards:newCard.next'),
                disabled: this.props.isCommunicating,
            },
            btnBack: {
                onClick: this.onBack,
            },
            isUserDataAvailable: ci && ci.address && ci.city && ci.firstName && ci.postalCode ? true : false,
            config,
        };

        return this.props.children(newProps);
    }
}

const mapStateToProps = (store: RootState) => ({
    isCommunicating: store.customerState.addValueAndCareCard.isCommunicating,
    serviceStations: store.appState.lookupValues.dealers,
    customerInfo: store.customerState.customerData.data.customerData,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    customerActions: bindActionCreators(Actions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(NewCardContainer);
